import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { SessionUser } from '@kjotkompanid/contracts';
import { masterApi } from './master-data-api';
import { createAppTheme } from './theme';

export type AppearanceMode = 'LIGHT' | 'DARK' | 'SYSTEM';

interface AppearanceContextValue {
  mode: AppearanceMode;
  setMode(mode: AppearanceMode): void;
  saving: boolean;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);
const namespace = 'ui.appearance.v1';

const asAppearanceMode = (value: unknown): AppearanceMode =>
  value === 'LIGHT' || value === 'DARK' || value === 'SYSTEM' ? value : 'SYSTEM';

export const useAppearance = (): AppearanceContextValue => {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error('AppearanceProvider is required');
  return context;
};

export function AppearanceProvider({
  user,
  children,
}: {
  user: SessionUser;
  children: React.ReactNode;
}): React.JSX.Element {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const preference = useQuery({
    queryKey: ['master-preference', namespace, user.id],
    queryFn: () => masterApi.preference(namespace),
  });
  const [mode, setLocalMode] = useState<AppearanceMode>('SYSTEM');
  const [revision, setRevision] = useState<number | null>(null);

  useEffect(() => {
    if (!preference.data) return;
    const value = preference.data.value as { mode?: unknown };
    setLocalMode(asAppearanceMode(value.mode));
    setRevision(preference.data.revision || null);
  }, [preference.data]);

  const save = useMutation({
    mutationFn: (next: AppearanceMode) =>
      masterApi.savePreference(namespace, {
        schemaVersion: 1,
        value: { mode: next },
        expectedRevision: revision,
      }),
    onSuccess: ({ revision: nextRevision }) => setRevision(nextRevision),
    onError: () => {
      if (preference.data) {
        setLocalMode(asAppearanceMode((preference.data.value as { mode?: unknown }).mode));
      }
    },
  });

  const setMode = (next: AppearanceMode): void => {
    setLocalMode(next);
    save.mutate(next);
  };
  const paletteMode: 'light' | 'dark' =
    mode === 'SYSTEM' ? (systemDark ? 'dark' : 'light') : mode === 'DARK' ? 'dark' : 'light';
  const theme = useMemo(() => createAppTheme(paletteMode), [paletteMode]);
  const value = useMemo(() => ({ mode, setMode, saving: save.isPending }), [mode, save.isPending]);

  return (
    <AppearanceContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppearanceContext.Provider>
  );
}
